import { AUTHORIZATION_HEADER } from '../authorization';
import { applySsrRequestHeaders } from './ssr-request-headers';

export type Req = {
  path: string;
  query?: URLSearchParams;
  headers?: Headers;
  signal?: AbortSignal;
} & (
  | {
      method: 'GET';
      body?: never;
    }
  | {
      method: 'POST';
      body?: unknown;
    }
  | {
      method: 'PATCH';
      body: unknown;
    }
  | {
      method: 'DELETE';
      body?: never;
    }
  | {
      method: 'PUT';
      body: unknown;
    }
);

function formatBody({ headers, body }: Req) {
  if (body === undefined || body === null || typeof body !== 'object') {
    return null;
  }

  const contentType =
    headers === undefined ? null : headers.get('Content-Type');
  if (contentType !== null && /x-www-form-urlencoded/.test(contentType)) {
    return new URLSearchParams(
      body as ConstructorParameters<typeof URLSearchParams>[0]
    ).toString();
  }
  return JSON.stringify(body);
}

async function formatResponse(resp: Response) {
  const { headers } = resp;
  const contentType = headers.get('Content-Type');
  if (contentType === 'application/json') {
    return await resp.json();
  }

  return await resp.text();
}

export type FetchCredentials = 'omit' | 'include';

type FetchOptions = {
  origin: string;
  credentials?: FetchCredentials;
};

function buildUrlPath(req: Pick<Req, 'path' | 'query'>, options: FetchOptions) {
  const { path, query } = req;
  const { origin } = options;

  let base = path;
  if (query) {
    base = `${base}?${query.toString()}`;
  }

  if (origin !== '') {
    base = `${origin}${base}`;
  }

  return base;
}

export async function fetchAdapter<T>(
  req: Req,
  options: FetchOptions
): Promise<{ status: number; data: T }> {
  const { method, headers: originalHeaders } = req;
  const { origin, credentials = 'include' } = options;
  let url = buildUrlPath(req, options);

  const headers = originalHeaders ?? new Headers();

  // Add default JSON headers if not already specified
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (!headers.has('AUTHORIZATION')) {
    headers.set('AUTHORIZATION', AUTHORIZATION_HEADER);
  }

  // BE requested that we include the full URL
  headers.set(
    'X-Requested-From',
    typeof window !== 'undefined' ? window.location.href : ''
  );

  // Tag SSR-originated requests so they're identifiable in API/Cloudflare logs.
  // No-ops in the browser.
  applySsrRequestHeaders(headers);

  const resp = await fetch(url, {
    method,
    headers,
    credentials: origin === '' ? undefined : credentials,
    body: formatBody(req),
    signal: req.signal,
  });
  const { status: respStatus } = resp;
  const json = await formatResponse(resp);
  return {
    status: respStatus,
    data: json,
  };
}

export type NetworkAdapter = typeof fetchAdapter;

let adapter: NetworkAdapter = fetchAdapter;

export function getAdapter() {
  return adapter;
}

export function setNetworkAdapter(next: NetworkAdapter) {
  adapter = next;
}
